import { FC, Suspense, useMemo } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useRecoilState } from 'recoil';
import { CircularProgress } from '@mui/material';

import './OneDayCalendarView.scss';
import { useAllPlanSuggestedModal, useEventDragAndDrop } from '../../../hooks';
import { getScheduleId } from '../../../components/Scheduler/AllPlanScheduler/helpers';
import { scheduleFallsOnDate } from '../../../helpers/allPlanSuggestions.helpers';
import {
  calendarViewSwitcherAtom,
  CalendarViewType,
  homeViewSwitcherAtom,
  useAssistantChat,
  selectedDateAtom,
} from '../../../data-access';
import { CalendarTimeline, CalendarViewSwitcher, GradientBorderButton } from '../../../components';
import { ReactComponent as ActionEntireDay } from '../../../assets/chat/action_entire_day.svg';
import { ReactComponent as SparkleIcon } from '../../../assets/scheduler/sparkle.svg';
import { useTranslation } from 'react-i18next';

interface CalendarViewProps {
  className?: string;
  noHeader?: boolean;
  calendarWidth?: number;
}

const OneDayCalendarView: FC<CalendarViewProps> = ({
  className,
  noHeader = false,
  calendarWidth,
}) => {
  const { t } = useTranslation();
  const [selectedDate] = useRecoilState(selectedDateAtom);
  const [__, setCalendarView] = useRecoilState(calendarViewSwitcherAtom);
  const [_, setCurrentContentView] = useRecoilState(homeViewSwitcherAtom);
  const { setOpenAssistantChat, startPlanMyDayIntro } = useAssistantChat();
  const {
    openAllPlanPanelInChatSidebar,
    isLoading: isReplanning,
    skipAllPlanning,
    schedules: allPlanSchedules,
    acceptedIds: allPlanAcceptedIds,
    discardedIds: allPlanDiscardedIds,
    targetDate: plannedTargetDate,
  } = useAllPlanSuggestedModal();
  const { onEventDrop, onEventResize, events } = useEventDragAndDrop();

  const handleViewChange = (view: CalendarViewType) => {
    setCurrentContentView('Calendar');
    setCalendarView(view);
  };

  const selectedDateKey = dayjs(selectedDate).format('YYYY-MM-DD');
  const isPastDate = dayjs(selectedDate).isBefore(dayjs(), 'day');
  const isReplanningSelectedDate =
    isReplanning && (!plannedTargetDate || plannedTargetDate === selectedDateKey);

  const pendingCount = useMemo(
    () =>
      allPlanSchedules.filter((schedule, index) => {
        if (!scheduleFallsOnDate(schedule, selectedDateKey)) {
          return false;
        }
        const id = getScheduleId(schedule, index);
        return !allPlanAcceptedIds.includes(id) && !allPlanDiscardedIds.includes(id);
      }).length,
    [allPlanAcceptedIds, allPlanDiscardedIds, allPlanSchedules, selectedDateKey]
  );

  const hasPendingReviews = pendingCount > 0 || isReplanningSelectedDate;
  const shouldShowPlanButton = !skipAllPlanning && !isPastDate;
  const shouldHidePlanMyDayText = typeof calendarWidth === 'number' && calendarWidth < 350;

  const handlePlanButtonClick = () => {
    setOpenAssistantChat(true);
    if (hasPendingReviews) {
      openAllPlanPanelInChatSidebar({ targetDate: selectedDateKey });
      return;
    }
    startPlanMyDayIntro(selectedDateKey);
  };

  return (
    <div className={clsx('calendar-view', className)} id="calendar-view">
      {!noHeader && (
        <div className="calendar-view__data-header">
          <div className="calendar-view__data-header__actions">
            {shouldShowPlanButton && (
              <GradientBorderButton
                className={clsx(
                  'calendar-view__data-header__plan-button',
                  shouldHidePlanMyDayText && 'calendar-view__data-header__plan-button--hidden',
                  isReplanningSelectedDate && 'calendar-view__data-header__plan-button--replanning'
                )}
                onClick={handlePlanButtonClick}
              >
                <div className="calendar-view__data-header__plan-button__icon-container">
                  {isReplanningSelectedDate ? (
                    <CircularProgress
                      size={14}
                      className="calendar-view__data-header__plan-button__icon"
                    />
                  ) : hasPendingReviews ? (
                    <SparkleIcon className="calendar-view__data-header__plan-button__icon" />
                  ) : (
                    <ActionEntireDay
                      className="calendar-view__data-header__plan-button__icon"
                      style={{ color: 'black' }}
                    />
                  )}
                  {!shouldHidePlanMyDayText && (
                    <span className="calendar-view__data-header__plan-button__text">
                      {isReplanningSelectedDate
                        ? t('calendar.plan.updating')
                        : hasPendingReviews
                          ? t('calendar.plan.reviewMoreEvents', { count: pendingCount })
                          : t('calendar.plan.planMyDay', { defaultValue: 'Plan my day' })}
                    </span>
                  )}
                </div>
              </GradientBorderButton>
            )}
            <CalendarViewSwitcher view={'day'} onViewChange={handleViewChange} />
          </div>
        </div>
      )}

      <Suspense
        fallback={
          <div className="calendar-view__loader">
            <CircularProgress />
          </div>
        }
      >
        <CalendarTimeline events={events} onEventDrop={onEventDrop} onEventResize={onEventResize} />
      </Suspense>
    </div>
  );
};

export default OneDayCalendarView;
